import { useTranslation } from 'react-i18next';
import Alert, { AlertType } from '@/components/ui/Alert';
import { putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { queryClient } from '@/hooks/QueryClient';
import { fundIsraeliIdChangeRequestsUrl } from '@/hooks/useFundIsraeliIdChangeRequests';
import {
  SupportedIsraeliIdChangeRequestDtoIn,
  SupportedIsraeliIdChangeRequestStatus,
} from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import styles from './IsraeliIdChange.module.css';

interface IsraeliIdChangeBannerProps {
  fundId: number;
  /** The open requests the coordinator was not told about yet: waiting, or decided and not yet seen. */
  requests: SupportedIsraeliIdChangeRequestDtoIn[];
  /** Turns on the board's «תיקון מספר זהות» filter. */
  onShow: () => void;
  /** These requests were told about - the line does not come back for them. */
  onSeen: (requests: SupportedIsraeliIdChangeRequestDtoIn[]) => void;
}

// Most pressing first: a rejection asks the coordinator to act, a correction only to know
const ORDER: { status: SupportedIsraeliIdChangeRequestStatus; type: AlertType }[] = [
  { status: 'Rejected', type: 'warning' },
  { status: 'Approved', type: 'success' },
  { status: 'Pending', type: 'info' },
];

/**
 * Above the supporteds board (HAT-1428), the way the standing-orders board says orders came back
 * from the bank: what happened to the fund's ID correction requests, said once (Chaya, 20.10).
 * «הצג» turns on the board's own filter, which stays like any other filter; after «הצג» or × the
 * line is gone for these requests.
 *
 * Closing it with a correction in it is having seen the correction - there is nothing to do about
 * one. A rejection still waits for «הבנתי» or a new request on the supported itself.
 */
const IsraeliIdChangeBanner = ({ fundId, requests, onShow, onSeen }: IsraeliIdChangeBannerProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const prefix = 'israeliIdChangeRequest.banner';

  const groups = ORDER
    .map(({ status, type }) => ({ status, type, group: requests.filter((r) => r.status === status) }))
    .filter(({ group }) => group.length > 0);

  if (groups.length === 0) return null;

  const acknowledge = async (approved: SupportedIsraeliIdChangeRequestDtoIn[]) => {
    const headers = { Authorization: `Bearer ${currentUser?.token}` };
    await Promise.all(approved.map((r) =>
      putAuth(`funds/${fundId}/supporteds/${r.contactId}/israeli-id-change-request/acknowledge`, headers)));
    await queryClient.invalidateQueries({ queryKey: [fundIsraeliIdChangeRequestsUrl(fundId)] });
  };

  return (
    <Alert
      type={groups[0].type}
      inline
      title={groups
        .map(({ status, group }) => t(`${prefix}.${status}`, { count: group.length, name: group[0].supportedName ?? '' }))
        .join(' ')}
      action={{
        label: t(`${prefix}.show`),
        onClick: () => {
          onSeen(requests);
          onShow();
        },
      }}
      onClose={() => {
        onSeen(requests);
        const approved = requests.filter((r) => r.status === 'Approved');
        if (approved.length > 0) void acknowledge(approved);
      }}
      className={styles.banner}
    />
  );
};

export default IsraeliIdChangeBanner;
